import { useEffect, useState } from "react";

import { getErrorMessage } from "@/core/services";
import { simularProjecao } from "@/modules/financeiro-obras/financeiro-obras.projecao.gateway";
import type {
  SimulacaoProjecao,
  SimularProjecaoBody,
} from "@/modules/financeiro-obras/financeiro-obras.projecao.types";

export const ATRASO_SIMULACAO_MS = 400;
export const SIMULACAO_ERRO = "Não foi possível calcular a prévia da projeção.";

type EstadoSimulacao = "ocioso" | "carregando" | "pronto" | "erro";

interface Resultado {
  estado: EstadoSimulacao;
  simulacao: SimulacaoProjecao | null;
  errorMessage: string | null;
}

const OCIOSO: Resultado = { estado: "ocioso", simulacao: null, errorMessage: null };

export function useSimulacaoProjecao(
  obraId: string,
  body: SimularProjecaoBody | null,
  atrasoMs: number = ATRASO_SIMULACAO_MS,
  recarga = 0,
) {
  const [resultado, setResultado] = useState<Resultado>(OCIOSO);
  const chave = body ? JSON.stringify(body) : null;

  useEffect(() => {
    if (chave === null) {
      setResultado(OCIOSO);
      return;
    }
    const controle = new AbortController();
    const corpo = JSON.parse(chave) as SimularProjecaoBody;
    setResultado((atual) => ({ ...atual, estado: "carregando", errorMessage: null }));

    const espera = setTimeout(() => {
      simularProjecao(obraId, corpo, { signal: controle.signal })
        .then((simulacao) => {
          if (!controle.signal.aborted)
            setResultado({ estado: "pronto", simulacao, errorMessage: null });
        })
        .catch((erro: unknown) => {
          if (controle.signal.aborted) return;
          setResultado((atual) => ({
            ...atual,
            estado: "erro",
            errorMessage: getErrorMessage(erro) || SIMULACAO_ERRO,
          }));
        });
    }, atrasoMs);

    return () => {
      clearTimeout(espera);
      controle.abort();
    };
  }, [obraId, chave, atrasoMs, recarga]);

  return resultado;
}
